
import { mapState, mapGetters } from 'vuex'
import { queueStatus as queueStatusEnum } from '~/enums/queueStatus'
import sharedMessages from '~/locales/common.translate'
import { ROLE_CAPTAIN, MatchState } from '~/utils/constants'
import { getErrorMessage } from '~/utils/errors'

export default {
	i18n: {
		sharedMessages,
	},
	computed: {
		...mapState(['scToken']),
		...mapState('event', ['activeSegment']),
		...mapGetters('event', ['rosterMinSize']),
		...mapState('match', ['showSelectFivePlayersForQueueModal']),
		...mapState('user', ['clanMembers', 'clan', 'userId', 'latestRoster']),
	},
	data: () => ({
		players: [],
		joinQueueLoading: false,
		validSelectedRoster: false,
	}),
	watch: {
		showSelectFivePlayersForQueueModal(val) {
			if (val) {
				this.joinQueueLoading = false
				this.validSelectedRoster = false
				const members = this.clanMembers.filter((m) => m.isRosterMember && m.userId)
				const readyMembers = members.filter((p) => p.townHallLevel === this.clan?.thLevel)

				// in case of the roster has more than max size
				// only the captain should be selected
				if (readyMembers.length !== this.rosterMinSize) {
					this.players = members.map((p) => {
						p.isSelected = this.isCaptain(p)
						return p
					})
					return
				}

				// in case of the roster has less than max size
				// all members should be selected
				this.players = members.map((p) => {
					p.isSelected = !!readyMembers.find((m) => m.tag === p.tag)
					return p
				})

				this.$nextTick(() => {
					this.onCheckboxInputChanged()
				})
			}
		},
	},
	mounted() {
		this.$nuxt.$on('checkbox-input-changed', () => {
			this.onCheckboxInputChanged()
		})
	},
	beforeDestroy() {
		this.$nuxt.$off('checkbox-input-changed')
	},
	methods: {
		isCaptain(player) {
			return (
				player.rosterPosition === ROLE_CAPTAIN &&
				player.townHallLevel === this.clan?.thLevel
			)
		},
		onCloseSelectFivePlayersForQueueModal() {
			this.joinQueueLoading = false
			this.validSelectedRoster = false
			this.players = this.clanMembers
				.filter((m) => m.isRosterMember && m.userId)
				.map((i) => {
					i.isSelected = this.isCaptain(i)
					return i
				})
			this.$store.commit('match/setShowSelectFivePlayersForQueueModal', false)
		},
		async selectPlayers() {
			this.joinQueueLoading = true
			const promises = []
			promises.push(this.$warApi.getClanWarStatus(this.clan.tag))
			promises.push(
				this.$api.matches.matchSeries.getAll({
					'page[size]': 1,
					'page[number]': 1,
					'filter[state]': MatchState.UPCOMING,
					'filter[user]': this.userId,
					...(this.latestRoster ? { 'filter[roster]': this.latestRoster.id } : null),
				})
			)
			promises.push(this.$scApi.getClanMembers(this.scToken))

			const [clanWarData, upcomingMatchResData, clanData] = await Promise.allSettled(promises)

			if (
				clanWarData.status !== 'fulfilled' ||
				upcomingMatchResData.status !== 'fulfilled' ||
				clanData.status !== 'fulfilled'
			) {
				let errorPath = '/error'
				if (clanWarData.status !== 'fulfilled') {
					errorPath = getErrorMessage(clanWarData.reason)
				}
				if (clanData.status !== 'fulfilled') {
					errorPath = getErrorMessage(clanData.reason)
				}
				this.$router.push(this.localePath(errorPath))
				this.onCloseSelectFivePlayersForQueueModal()
				return
			}

			if (clanWarData.value.data.clanInWar !== false) {
				this.$router.push(this.localePath('/error/23'))
				this.onCloseSelectFivePlayersForQueueModal()
				return
			}

			if (upcomingMatchResData.value.data?.length > 0) {
				this.$router.push(this.localePath('/error/25'))
				this.onCloseSelectFivePlayersForQueueModal()
				return
			}

			const selectedPlayers = this.players.filter((p) => p.isSelected).map((p) => p.tag)

			try {
				await this.$warApi.setSelectedPlayers(this.scToken, this.clan.tag, selectedPlayers)
			} catch (e) {
				const errorPath = getErrorMessage(e)
				this.$router.push(this.localePath(errorPath))
				this.onCloseSelectFivePlayersForQueueModal()
				return
			}

			try {
				await this.$scApi.checkSelectedPlayers(this.scToken)
			} catch (e) {
				const errorPath = getErrorMessage(e)
				this.$router.push(this.localePath(errorPath))
				this.onCloseSelectFivePlayersForQueueModal()
				return
			}

			try {
				await this.$api.matches.matchSeries.joinQueue(this.activeSegment.id)
				this.$store.commit('user/setQueueStatus', queueStatusEnum.IN_QUEUE)
			} catch (err) {
				if (err?.response?.status === 400) {
					const errorMessage = err?.response?.data?.errors?.[0]?.detail

					// full message contains dynamic data
					// e.g: This roster has already played 14 matches in last 86400000 seconds
					if (errorMessage.includes('roster has already played')) {
						this.$router.push(this.localePath('/error/26'))
						this.onCloseSelectFivePlayersForQueueModal()
						return
					}
				}

				const errorPath = getErrorMessage(err)
				this.$router.push(this.localePath(errorPath))
				this.onCloseSelectFivePlayersForQueueModal()
				return
			}

			this.onCloseSelectFivePlayersForQueueModal()
		},
		onCheckboxInputChanged() {
			const amountPlayers = this.players.filter((p) => p.isSelected).length
			this.validSelectedRoster = amountPlayers === this.rosterMinSize
		},
	},
}
